import { useState, type FormEvent } from "react";
import { ArrowRight, CheckCircle2, MailIcon } from "lucide-react";

import { Logo } from "@/components/alia/logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { auth } from "@/lib/api";


export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email) return;

    setError(null);
    setSubmitting(true);
    try {
      await auth.requestMagicLink(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No pudimos enviar el link. Intenta de nuevo."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative grid min-h-screen lg:grid-cols-2">
      {/* Left — form */}
      <div className="relative flex flex-col px-6 py-8 sm:px-10 lg:px-16">
        <div className="flex items-center justify-between">
          <Logo size={28} />
          <a
            href="https://aliadigital.co"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            ← Volver a aliadigital.co
          </a>
        </div>

        <div className="flex flex-1 items-center">
          <div className="w-full max-w-sm">
            {!sent ? (
              <>
                <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                  Bienvenido de vuelta
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Ingresa tu correo y te enviamos un link para entrar. Sin
                  contraseñas.
                </p>

                <form onSubmit={onSubmit} className="mt-8 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Correo</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      required
                      placeholder="tu@empresa.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={submitting}
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-destructive">{error}</p>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={submitting || !email}
                  >
                    {submitting ? "Enviando…" : "Enviar link"}
                    <ArrowRight className="size-4" />
                  </Button>
                </form>

                <p className="mt-8 text-xs leading-relaxed text-muted-foreground">
                  Al continuar aceptas los Términos y la Política de Privacidad
                  de Alia.
                </p>
              </>
            ) : (
              <div className="space-y-5">
                <div className="flex size-12 items-center justify-center rounded-2xl border border-success/30 bg-success/10">
                  <CheckCircle2 className="size-6 text-success" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                    Revisa tu correo
                  </h1>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    Te enviamos un link a{" "}
                    <span className="font-medium text-foreground">{email}</span>
                    . Ábrelo en este mismo dispositivo. El link expira en 15
                    minutos.
                  </p>
                </div>

                <div className="rounded-lg border border-border bg-surface p-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2 font-medium text-foreground">
                    <MailIcon className="size-3.5" />
                    ¿No te llegó?
                  </div>
                  <p className="mt-1.5">
                    Revisa spam o promociones. Si pasaron 2 minutos sin
                    recibirlo,{" "}
                    <button
                      onClick={() => setSent(false)}
                      className="text-brand hover:underline"
                    >
                      vuelve a intentarlo
                    </button>
                    .
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} Alia · Hecho en Colombia
        </p>
      </div>

      {/* Right — signature gradient panel */}
      <div className="relative hidden overflow-hidden border-l border-border bg-signature lg:block">
        <div className="absolute inset-0 bg-grid-faint opacity-30" />

        {/* Diagonal light streaks reminiscent of the marketing site */}
        <div
          className="absolute -right-1/4 top-0 h-[180%] w-1/2 rotate-[18deg] opacity-60 blur-3xl"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, var(--color-accent-blue) 30%, var(--color-accent-orange) 75%, transparent 100%)",
          }}
        />

        <div className="relative flex h-full flex-col justify-end p-16">
          <blockquote className="space-y-4">
            <p className="text-2xl font-medium leading-snug text-foreground">
              <span className="text-gradient-brand">
                Visitantes entran, clientes salen.
              </span>
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Allison atiende, califica y agenda 24/7. Tú solo cierras.
            </p>
          </blockquote>

          <div className="mt-10 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="size-1.5 rounded-full bg-success" />
              Status: operacional
            </span>
            <span>·</span>
            <span>v0.1 · Sprint 1</span>
          </div>
        </div>
      </div>
    </div>
  );
}
